<template>
<m-pageframe
    :title="title"
>
<v-container
    v-if="template != null"
>
    <!-- Back button to navigate to auto-messages home -->
    <div style="margin-bottom: 20px;">
        <v-btn
            color="primary"
            outlined
            @click="onClickBackToAutoMessages"
        >
            <v-icon left>mdi-arrow-left</v-icon>
            Back to Templates
        </v-btn>
    </div>

    <v-alert
        v-if="show_red_chirp_staff_no_edit_in_auto_mode"
        border="left"
        colored-border
        color="primary"
        elevation="0"
        class="my-4 shaded-light"
    >
        <div class="d-flex align-center justify-space-between">
            <div>
                <v-icon color="primary" class="ml-2 mr-3" aria-hidden="true">
                    mdi-pencil-off
                </v-icon>
                <strong>Editing Disabled:</strong> RedChirp staff may not edit an automated message that is in automatic mode
            </div>
            <v-btn
                color="primary"
                elevation="0"
                outlined
                @click="onClickPlaceIntoManualMode"
            >
                <v-icon aria-hidden="true" left>mdi-pencil</v-icon>
                Place into Manual Mode
            </v-btn>
        </div>
    </v-alert>

    <m-beta-alert
        v-if="template.template_context == 'order-fulfilled-follow-up'"
        code="beta-order-fulfilled-message"
        description="<b>Heads up!</b><br/><br/>This automated message is stil new (i.e. in 'beta').  We have done our best to test these new messages out thoroughly, but they <i>might</i> still have a few bugs.  We are eager to get your feedback."
    />

    <m-beta-alert
        v-if="template.template_context == 'order-placed-request-opt-in'"
        code="beta-order-placed-request-opt-in"
    />

    <div
        v-if="is_second_generation_shipping_message"
    >
        <b>When will this message be sent</b>
        <div
            v-if="template.template_context == 'shipping-new-tracking-number'"
        >
            <ul>
                <li>RedChirp sends this message whenever it observes a new tracking number</li>
                <li>If the tracking number is associated with a shipment scheduled for a future date, RedChirp will delay sending the message until the scheduled ship date arrives</li>
                <ul>
                    <li>If you change the scheduled ship date, RedChirp adjusts accordingly and will send the message on the updated ship date</li>
                </ul>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-exception'"
        >
            <ul>
                <li>RedChirp sends this message whenever it observes a shipping exception (for example, that a package is undeliverable because the address does not exist)</li>
                <li>There are a wide variety of exceptions that the carriers encounter &mdash; some are serious, such a package has been lost, while others are comparatively minor, such as a hours delay due to bad weather.</li>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-on-weather-hold'"
        >
            <ul>
                <li>RedChirp sends this message whenever it learns that a package has been put on a weather hold (e.g. held in the warehouse because the destination is too hot for wine or other temperature-senstive goods)</li>
                <li>You must have an integration enabled that provides RedChirp with detailed tracking information for the pacakge in question</li>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-weather-hold-release'"
        >
            <ul>
                <li>RedChirp sends this message whenever it learns that a package that was previously on weather hold is released for shipment</li>
                <li>After release, other shipping lifecycle events (e.g. 'Shipping: First In Transit') will still be sent</li>
                <li>You must have an integration enabled that provides RedChirp with detailed tracking information for the pacakge in question</li>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-first-in-transit'"
        >
            <ul>
                <li>RedChirp sends this message whenever it learns that a package has had its first in-transit event (e.g. picked up by carrier, etc.)</li>
                <li>You must have an integration enabled that provides RedChirp with detailed tracking information for the pacakge in question</li>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-out-for-delivery'"
        >
            <ul>
                <li>RedChirp sends this message whenever it learns that a package is out for delivery</li>
                <li>You must have an integration enabled that provides RedChirp with detailed tracking information for the pacakge in question</li>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-delivery-attempt-failed'"
        >
            <ul>
                <li>RedChirp sends this message whenever it learns that a delivery attempt for a package has failed</li>
                <li>You must have an integration enabled that provides RedChirp with detailed tracking information for the pacakge in question</li>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-available-for-pickup'"
        >
            <ul>
                <li>RedChirp sends this message whenever it learns that a package is being held for pickup by the customer (e.g. the package is at a FedEx "hold at" location, etc.)</li>
                <li>You must have an integration enabled that provides RedChirp with detailed tracking information for the pacakge in question</li>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-delivered'"
        >
            <ul>
                <li>RedChirp sends this message whenever it learns that a package has been delivered to a customer</li>
                <li>You must have an integration enabled that provides RedChirp with detailed tracking information for the pacakge in question</li>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-picked-up'"
        >
            <ul>
                <li>RedChirp sends this message whenever it learns that a package has been picked up by a customer</li>
                <li>You must have an integration enabled that provides RedChirp with detailed tracking information for the pacakge in question</li>
            </ul>
        </div>
        <div
            v-if="template.template_context == 'shipping-expected-delivery-date-changed'"
        >
            <ul>
                <li>RedChirp sends this message whenever it learns that the expected delivery date of a package has changed</li>
                <li>This message is <b>not</b> sent when RedChirp <i>first</i> learns that a package has an expected delivery date</li>
            </ul>
        </div>

    </div>

    <div
        v-if="template.template_context == 'custom_automated_message'"
        style="margin-bottom: 15px;"
    >

        <b>When will this message be sent</b>

        <ul>
            <li>When triggered by a RESTful api call to <code>{{custom_webhook_url}}</code></li>
        </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-reservation-ecellar-booked'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will send this message whenever a new reservation is created in eCELLAR that is in the <b>booked</b> state</li>
        <li>RedChirp will send this message whenever an existing reservation is modified to be in the <b>booked</b> state (i.e was not in that state before but is now)</li>
        <li>The message will be sent once per <b>reservation</b> -- it will never be sent more than once per reservation</li>
        <li><b>Please note</b> that this automated message does not preclude sending reservation created and reservation updated messages.  Generally, it is not advisable to use this message in combination with those other messages.</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-credit-card-decline'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will send this message whenever a new tender fails because a card was declined</li>
        <li>Failure must be observed by RedChirp within 48 hours or no message will be sent</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-reservation-ecellar-requested'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will send this message whenever a new reservation is created in eCELLAR that is in the <b>requested</b> state</li>
        <li>RedChirp will send this message whenever an existing reservation is modified to be in the <b>requested</b> state (i.e was not in that state before but is now)</li>
        <li>The message will be sent once per <b>reservation</b> -- it will never be sent more than once per reservation</li>
        <li><b>Please note</b> that this automated message does not preclude sending reservation created and reservation updated messages.  Generally, it is not advisable to use this message in combination with those other messages.</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-reservation-ecellar-visited'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will send this message whenever a new reservation is created in eCELLAR that is in the <b>visited</b> state</li>
        <li>RedChirp will send this message whenever an existing reservation is modified to be in the <b>visited</b> state (i.e was not in that state before but is now)</li>
        <li>The message will be sent once per <b>reservation</b> -- it will never be sent more than once per reservation</li>
        <li><b>Please note</b> that this automated message does not preclude sending reservation created and reservation updated messages.  Generally, it is not advisable to use this message in combination with those other messages.</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-reservation-ecellar-tentative'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will send this message whenever a new reservation is created in eCELLAR that is in the <b>tentative</b> state</li>
        <li>RedChirp will send this message whenever an existing reservation is modified to be in the <b>tentative</b> state (i.e was not in that state before but is now)</li>
        <li>The message will be sent once per <b>reservation</b> -- it will never be sent more than once per reservation</li>
        <li><b>Please note</b> that this automated message does not preclude sending reservation created and reservation updated messages.  Generally, it is not advisable to use this message in combination with those other messages.</li>
    </ul>

    </div>



    <div
        v-if="template.template_context == 'auto-message-provide-tracking-number'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will send this message whenever it observes a shipment in your CRM (e.g. Commerce7, etc.) <b>with a tracking number</b></li>
        <li>RedChirp will only send this message if it becomes aware of the shipment on the day it was made or the calendar day prior -- if you enter shipments more than two days after they are made, automated messages <b>will not go out</b></li>
        <li>The message will be sent once per <b>shipment</b> -- if an order has more than one shipment then this message will be sent multiple times for that <b>order</b> (once per <b>shipment</b>)</li>
        <li>The message will be sent to all phone numbers that appear on the order</li>
    </ul>

    </div>


    <div
        v-if="template.template_context == 'auto-message-reservation-reminder-almost-time'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will send this message as early as two hours before the reservation time and as late as thirty minutes before the reservation time</li>
        <li>RedChirp will try and send this message as early as possible:  Under most circumstances, this message will be sent two hours before the reservation time</li>
        <li>The message will only be sent once per reservation (even if the reservation is rescheduled etc.)</li>
        <li v-if="is_ecellar_enabled"><b>eCELLAR Specific:</b>  RedChirp will only send this message if the reservation is in the <b>booked</b> state</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-reservation-reminder-morning-of'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>Every day, between 8:00 AM and 10:00 AM, RedChirp will send messages for that day's reservations</li>
        <li>RedChirp will try and send this message as soon as possible:  Under most circumstances, this message will go out between 8:00 AM and 8:15 AM on the day of a reservation</li>
        <li>This message will not be sent for reservations with a scheduled time before 8:00 AM</li>
        <li>The message will only sent once per reservation (even if the reservation is rescheduled etc.)</li>
        <li v-if="is_ecellar_enabled"><b>eCELLAR Specific:</b>  RedChirp will only send this message if the reservation is in the <b>booked</b> state</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-reservation-reminder-day-before'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>Every day, between 11:00 AM and 4:00 PM, RedChirp will send messages for tomorrow's reservations</li>
        <li>RedChirp will try and send this message as soon as possible:  Under most circumstances, this message will go out between 11:00 AM and 11:30 AM the day before a reservation</li>
        <li>The message will only sent once per reservation (even if the reservation is rescheduled etc.)</li>
        <li v-if="is_ecellar_enabled"><b>eCELLAR Specific:</b>  RedChirp will only send this message if the reservation is in the <b>booked</b> state</li>
    </ul>

    </div>

     <div
        v-if="template.template_context == 'auto-message-reservation-reminder-two-days-before'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>Every day, between 11:00 AM and 4:00 PM, RedChirp will send messages for reservations that are two days from now</li>
        <li>RedChirp will try and send this message as soon as possible:  Under most circumstances, this message will go out between 11:00 AM and 11:30 AM the two days before a reservation</li>
        <li>The message will only sent once per reservation (even if the reservation is rescheduled etc.)</li>
        <li v-if="is_ecellar_enabled"><b>eCELLAR Specific:</b>  RedChirp will only send this message if the reservation is in the <b>booked</b> state</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-reservation-at-start-time'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will try and send this message as soon as possible after a reservation start time.</li>
        <li>Under most circumstances, this message will go out within 10 minutes of a reservation's scheduled start time</li>
        <li>The message will <b>not</b> be sent for cancelled or tentative reservations</li>
        <li>The message will only sent once per reservation (even if the reservation is rescheduled etc.)</li>
        <li>You can use this message to create a follow up at any desired time following a reservation by setting the "Delay before sending" field to your desired follow up time</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-reservation-day-after'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>Every day, between 11:00 AM and 4:00 PM, RedChirp will send messages for non-cancelled reservations the day after the reservation time (i.e. yesterday's reservations)</li>
        <li>RedChirp will try and send this message as soon as possible:  Under most circumstances, this message will go out between 11:00 AM and 11:30 AM the day after a reservation</li>
        <li>The message will only sent once per reservation (even if the reservation is rescheduled etc.)</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'auto-message-reservation-reminder-week-before'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>Every day, between 2:30 PM and 4:00 PM, RedChirp will send messages for reservations scheduled for 7 days hence</li>
        <li>RedChirp will try and send this message as soon as possible:  Under most circumstances, this message will go out between 2:30 PM and 2:45 PM, 7 days before a reservation's date</li>
        <li>The message will only sent once per reservation (even if the reservation is rescheduled etc.)</li>
    </ul>

    </div>

     <div
        v-if="template.template_context == 'auto-message-reservation-reminder-month-before'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>Every day, between 2:30 PM and 4:00 PM, RedChirp will send messages for reservations scheduled for 30 days hence</li>
        <li>RedChirp will try and send this message as soon as possible:  Under most circumstances, this message will go out between 2:30 PM and 2:45 PM, 30 days before a reservation's date</li>
        <li>The message will only sent once per reservation (even if the reservation is rescheduled etc.)</li>
        <li v-if="is_ecellar_enabled"><b>eCELLAR Specific:</b>  RedChirp will only send this message if the reservation is in the <b>booked</b> state</li>
    </ul>

    </div>

    <div
        v-if="commerce7_email_when_will_it_send != null"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>In Commerce7, you can view and configure Transaction Emails from Settings (left navigation) > Transaction Emails</li>
        <li>For details on when Commerce7 triggers a particular transactional email, you can consult the <a href="https://documentation.commerce7.com/setting-up-transactional-emails#trigger" target="_blank">the Commerce7 Transaction email types &amp; triggers documentation.</a></li>
        <li>This message is sent when Commerce7 sends the transaction email "<b>{{commerce7_email_when_will_it_send.label}}</b>" </li>
        <li v-if="commerce7_email_when_will_it_send.fourteen_day_limit">The message will not be sent to the same customer more than once in a <b>14 day period.</b></li>
        <li>The message will be sent as soon as Commerce7 notifies us the email has been sent.</li>
        <li>If it is outside of the Send During Hours you have set, RedChirp will hold the message for a maximum of one day and send as soon as it is during the Send During Hours</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'birthday-message' || template.template_context == 'birthday-month-message'"
        style="margin-bottom: 15px;"
    >
        <b>When will this message be sent</b>
        <div v-if="template.template_context == 'birthday-message'">
            <ul>
                <li>RedChirp sends this message to customers on their birthday (adjusted by the "Day to send relative to birthday" parameter)</li>
                <li>You can set a delay (positive or negative) to send messages before or after the actual birthday</li>
            </ul>
        </div>
        <div v-if="template.template_context == 'birthday-month-message'">
            <ul>
                <li>RedChirp sends this message to all customers with birthdays in the current month</li>
                <li>The message is sent on a specific day of the month (configured in the "Day of month to send" parameter)</li>
                <li>This is useful for sending a single monthly message to all customers celebrating birthdays that month</li>
            </ul>
        </div>
    </div>
    
    <div
        v-if="template.template_context == 'order-placed-request-opt-in'"
        style="margin-bottom: 15px;"
    >
      <b>Order Placed: Request Opt-In Message:</b>
      <ul style="margin-top: 10px; margin-bottom: 0;">
        <li>RedChirp will try to send this message when an order is placed in your connected POS / CRM system</li>
            <ul>
                <li>By default, this includes club orders.</li>
                <li>You can change this behavior by creating a conditional message</li>
            </ul>
        <li>RedChirp will use smart routing when both "Order Placed" and "Order Placed: Request Opt In" messages are enabled:
            <ul>
                <li>If the recipient has promotional consent on file, the regular "Order Placed" message will typically be sent instead</li>
                <li>If the recipient does not have promotional consent, this "Request Opt In" message will typically be sent</li>
                <li>This approach is designed to help ensure customers with consent receive order confirmations while others receive opt-in requests</li>
            </ul>
        </li>
        <li>Sending this message starts a double opt-in flow:
          <ul>
            <li>Initially, automated processes try to opt the recipient out of promotional messaging until they reply YES</li>
          </ul>
        </li>
        <li>RedChirp will attempt to avoid sending this message when:
          <ul>
            <li>If the order total is less than $1.00</li>
            <li>The recipient already has promotional consent</li>
            <li>If the phone number has received this message within the last 7 days</li>
          </ul>
        </li>
        <li>
             We recommend against adding any promotional, incentive, or marketing language to this message
        </li>
      </ul>

     

    </div>
    
    <div
        v-if="template.template_context == 'wine-club-new-membership'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>Within two hours after the creation of a new club membership, RedChirp will send this message</li>
        <li>The message will be sent to every phone number associated with the member's customer profile</li>
        <li>The message will only sent once per day per phone number &mdash; therefore, if someone joins more than one club on the same day they will only receive this message once</li>
    </ul>

    </div>

    <div
        v-if="template.template_context == 'wine-club-join-follow-up'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will trigger this message after the specified (customizable) number of days after a member signs up for a club</li>
        <li>The membership must be active on the day in question</li>
        <li>The message will be sent to every phone number associated with the member's customer profile</li>
        <li>The message will only sent once per day per phone number &mdash; therefore, if someone has more than one club join follow up on the same day they will only receive this message once</li>
        <li>RedChirp generates these messages gradually between 9:00 AM and 2:00 PM each day</li>
    </ul>

    </div>


    <div
        v-if="template.template_context.includes('wine-shipping')"
        style="margin-bottom: 15px;"
    >

    <b>Notes on this automated message</b>

    <ul>
        <li v-if="template.template_context == 'wine-shipping-info-received'">RedChirp will send this message whenever Wineshipping reports that a package's information has been received (label created)</li>
        <li v-if="template.template_context == 'wine-shipping-info-received'">If your organization creates labels far in advance of shipping, consider using the Wineshipping: First In Transit Event Observed message instead</li>

        <li v-if="template.template_context == 'wine-shipping-first-in-transit'">RedChirp will send this message whenever Wineshipping reports that the first in-transit event for the package</li>
        <li v-if="template.template_context == 'wine-shipping-first-in-transit'">Use this message when your organization is in the habit of creating labels far in advance of shipping the item</li>

        <li v-if="template.template_context == 'wine-shipping-delivered'">RedChirp will send this message whenever Wineshipping reports that a package has been delivered</li>
        <li v-if="template.template_context == 'wine-shipping-available-for-pickup'">RedChirp will send this message whenever Wineshipping reports that a package is ready for pickup</li>
        <li v-if="template.template_context == 'wine-shipping-out-for-delivery'">RedChirp will send this message whenever Wineshipping reports that a package is out for delivery</li>
        <li v-if="template.template_context == 'wine-shipping-delivery-attempt-failed'">RedChirp will send this message whenever Wineshipping reports that a delivery attempt failed</li>
        <li v-if="template.template_context == 'wine-shipping-exception'">RedChirp will send this message whenever Wineshipping reports that an exception (e.g. weather delay, package returned, etc.) has occoured.</li>
        <li>The message will go to each number listed in the Wineshipping's <code>smses</code> field</li>
        <li>The customer name comes from Wineshipping's one <code>customer_name</code> field</li>
    </ul>

    </div>

    <div
        v-if="template.template_context.includes('wine-co-')"
        style="margin-bottom: 15px;"
    >

    <b>Notes on this automated message</b>

    <ul>
        <li v-if="template.template_context == 'wine-co-shipped'">RedChirp will send this message whenever Wine Co reports that a package's information has been received (label created)</li>
        <li v-if="template.template_context == 'wine-co-delivered'">RedChirp will send this message whenever Wine Co reports that a package has been delivered</li>
        <li v-if="template.template_context == 'wine-co-available-for-pickup'">RedChirp will send this message whenever Wine Co reports that a package is ready for pickup</li>
        <li v-if="template.template_context == 'wine-co-out-for-delivery'">RedChirp will send this message whenever Wine Co reports that a package is out for delivery</li>
        <li v-if="template.template_context == 'wine-co-delivery-attempt-fail'">RedChirp will send this message whenever Wine Co reports that a delivery attempt failed</li>
        <li v-if="template.template_context == 'wine-co-picked-up'">RedChirp will send this message whenever Wine Co reports that a package has been picked up</li>

        <li>The message will go to each number listed in Wine Co's <code>home_phone</code>, <code>work_phone</code> and/or <code>mobile_phone</code> fields</li>
        <li>The customer name comes from Wineshipping's one <code>recipient_first_name</code> and <code>recipient_last_name</code> field</li>
    </ul>

    </div>

    <div
        v-if="template.template_context.includes('ship-engine')"
        style="margin-bottom: 15px;"
    >

    <b>Notes on this automated message</b>

    <ul>
        <li><b>Fee Notice:</b> When you have the ShipEngine API turned on, there is a cost of $0.02 (two cents) per package shipped -- this cost is per package, not per message sent.  Therefore, if one package triggered several automated messages (e.g. out for delivery, delivered) you would only be charged one fee of once cent.  These fees only apply to packages that are shipped while the integration is enabled.  You can disable this integration at any time.</li>
        <li v-if="template.template_context == 'ship-engine-shipped'">RedChirp will send this message whenever ShipEngine reports that a package's information has been received (label created)</li>
        <li v-if="template.template_context == 'ship-engine-delivered'">RedChirp will send this message whenever ShipEngine reports that a package has been delivered</li>
        <li v-if="template.template_context == 'ship-engine-available-for-pickup'">RedChirp will send this message whenever ShipEngine reports that a package is ready for pickup</li>
        <li v-if="template.template_context == 'ship-engine-out-for-delivery'">RedChirp will send this message whenever ShipEngine reports that a package is out for delivery</li>
        <li v-if="template.template_context == 'ship-engine-delivery-attempt-fail'">RedChirp will send this message whenever ShipEngine reports that a delivery attempt failed</li>
        <li v-if="template.template_context == 'ship-engineo-picked-up'">RedChirp will send this message whenever ShipEngine reports that a package has been picked up</li>
        <li>The message will go to each number listed in your paired (e.g. Commerce7, eCellar, OrderPort, WineDirect, etc.) POS order data.  <b>Messages will be sent to both bill to and ship to contacts on the order.</b></li>
    </ul>

    </div>



    <div
        v-if="template.template_context == 'list-drip'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li
            v-if="template.list_drip_delay != null && template.list_drip_delay < 60000"
        >
            RedChirp will send this message shortly after a member is added to the list <code>{{template.list_drip_list_internal_label}}</code>
        </li>

        <li
           v-else
        >
            RedChirp will send this message {{$rc.pretty(template.list_drip_delay,'duration')}} after a member is added to the list <code>{{template.list_drip_list_internal_label}}</code>
        </li>

        <li>
            The send time is <em>approximate</em>.  Messages may be delayed up to one hour.
        </li>

    </ul>

    </div>

    <div
        v-if="template.template_context == 'order-placed'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp tries to send this message when an order is placed in your connected POS / CRM system</li>
        <li>RedChirp will use smart routing when both "Order Placed" and "Order Placed: Request Opt In" messages are enabled:
            <ul>
                <li>If the recipient has promotional consent on file, this "Order Placed" message will typically be sent</li>
                <li>If the recipient does not have promotional consent, the "Order Placed: Request Opt In" message will typically be sent instead</li>
                <li>This approach is designed to help ensure customers with consent receive order confirmations while others receive opt-in requests</li>
            </ul>
        </li>
        <li>RedChirp will trigger this message when an order is placed</li>
        <ul>
            <li><b>Please note:</b> this message is triggered when an order is <b>placed</b>.</li>
            <li>Importantly, orders are placed <b>before</b> they are tendered (paid).</li>
            <li>Keep in mind that an order may be placed and then the payment method may, for example, decline.</li>
        </ul>
        <li>This message is triggered for all order types</li>
        <ul>
            <li>Keep this in mind when considering this message for club shipments; if needed, you can use conditions to exclude or change the text for club orders. </li>
            <li>Keep this in mind if you are creating orders for reservations / events; if needed, you can use conditions to exclude or change the text in this circumstance.</li>
        </ul>

    </ul>

    </div>

    <div
        v-if="template.template_context == 'order-fulfilled-follow-up'"
        style="margin-bottom: 15px;"
    >

    <b>When will this message be sent</b>

    <ul>
        <li>RedChirp will trigger this message when it has been the specified number of days between the first fulfillment date associated with this order</li>
        <ul>
            <li>This message will only trigger one time per order</li>
            <li>This message is sent to every phone number associated with the order</li>
            <li>This message will not be sent if the order is cancelled</li>
        </ul>

        <li>Depending on how you use your POS / CRM, it is possible that your orders may not have fulfilment dates &mdash; as a result <b>this message may not send for all orders</b></li>
        <ul>
            <li>To check the first fulfment dates of your orders go to Main Menu > [Your CRM Name, e.g. Commerce7, WineDirect, etc.] > Orders and look at the date in the first fulfillment column</li>
            <li>If this column is blank no follow up message will be sent</li>
            <li>You may need to do a re-sync of your CRM data for dates to populate &mdash;  RedChirp automatically triggers a full sync when you first turn this feature on, give it a day or so to complete</li>
            <li>When checking your orders over, be sure to consider different types of orders: club orders, pickup orders, carry out orders and shipped orders may behave differently, depending on how you use your CRM</li>
        </ul>

        <li>This message is triggered for all order types</li>
        <ul>
            <li>Keep this in mind when considering this message for club shipments; if needed, you can use conditions to exclude or change the text for club orders. </li>
            <li>Keep this in mind if you are creating orders for reservations / events; if needed, you can use conditions to exclude or change the text in this circumstance.</li>
        </ul>

    </ul>

    <br/>

    </div>

    <div
        v-if="(template.template_context == 'birthday-message' || template.template_context == 'birthday-month-message') || is_second_generation_shipping_message || template.template_context == 'order-fulfilled-follow-up' || template.template_context == 'order-placed' || template.template_context == 'order-placed-request-opt-in'"
        style="margin-top: 15px; margin-bottom: 15px;"
    >
        <b>Frequently Asked Questions (FAQ)</b>
        <div style="margin-left: 15px">
            <div v-if="template.template_context == 'birthday-message' || template.template_context == 'birthday-month-message'">
                <m-faq>
                    <template v-slot:question>When will this message be sent?</template>
                    <template v-slot:answer>
                        <div v-if="template.template_context == 'birthday-message'">
                            <p>
                                Birthday messages are sent based on the "Day to send relative to birthday" setting:
                            </p>
                            <ul>
                                <li><b>Positive value:</b> Sends the message that many days AFTER the birthday</li>
                                <li><b>Zero (0):</b> Sends the message ON the birthday</li>
                                <li><b>Negative value:</b> Sends the message that many days BEFORE the birthday</li>
                            </ul>
                        </div>
                        <div v-if="template.template_context == 'birthday-month-message'">
                            <p>
                                Birthday month messages are sent on the specified day of the month (1-27) to all customers with birthdays in that month.
                            </p>
                        </div>
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>What's the difference between informational and promotional message types?</template>
                    <template v-slot:answer>
                        <p>
                            <b>Informational messages</b> are transactional in nature and provide important information about orders, shipments, reservations, or account updates. Examples include:
                        </p>
                        <ul>
                            <li>Order confirmations and shipping notifications</li>
                            <li>Appointment or reservation reminders</li>
                            <li>Account updates or security alerts</li>
                            <li>Customer service responses</li>
                        </ul>
                        <p>
                            <b>Promotional messages</b> are promotional in nature and advertise products, services, or events. Examples include:
                        </p>
                        <ul>
                            <li>Sales announcements and special offers</li>
                            <li>New product launches</li>
                            <li>Event invitations</li>
                            <li>Promotional campaigns</li>
                        </ul>
                    </template>
                </m-faq>
                <m-faq>
                    <template v-slot:question>Which integrations can provide birthday data?</template>
                    <template v-slot:answer>
                        This message can be triggered by the following integrations:
                        <m-integrations-that-support
                            concept="birthday"
                        />
                        <p></p>
                        <p>
                            For these messages to work, your customers must have birthdate information stored in one of these integrated systems.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>Why would I want to ignore certain birth dates?</template>
                    <template v-slot:answer>
                        <p>
                            The "Ignored Birth Dates" feature allows you to exclude specific dates from triggering birthday messages. This is useful for:
                        </p>
                        <ul>
                            <li>Excluding default or placeholder dates that some CRM systems automatically assign (like January 1st)</li>
                            <li>Avoiding sending messages on dates when you know your system contains test or dummy data</li>
                        </ul>
                        <p></p>
                        <p>
                            Any customer with a birthday matching an ignored date will not receive birthday messages. This filter only applies to customers with full birth date (including year) in our system.
                        </p>
                    </template>
                </m-faq>

                <m-faq v-if="template.template_context == 'birthday-message'">
                    <template v-slot:question>What does the "Day to send relative to birthday" parameter do?</template>
                    <template v-slot:answer>
                        <p>
                            For <b>Birthday Messages</b>, the "Delay Days" parameter allows you to adjust when the message is sent relative to the actual birthday:
                        </p>
                        <ul>
                            <li><b>Positive value (e.g., 1):</b> Sends the message that many days AFTER the birthday</li>
                            <li><b>Zero (0):</b> Sends the message ON the birthday</li>
                            <li><b>Negative value (e.g., -1):</b> Sends the message that many days BEFORE the birthday</li>
                        </ul>
                        <p>
                            For example, setting a delay of -1 would send birthday messages one day before the actual birthday, which is useful for sending "birthday eve" messages or ensuring customers receive messages before their special day.
                        </p>
                    </template>
                </m-faq>

                <m-faq v-if="template.template_context == 'birthday-month-message'">
                    <template v-slot:question>What does the "Day of month to send" parameter do?</template>
                    <template v-slot:answer>
                        <p>
                            For <b>Birthday Month Messages</b>, this parameter specifies the day of the month (1-27) when the message should be sent to all customers with birthdays in that month. We recommend setting this to 2 to ensure the message is sent early in the month before most birthdays occur.
                        </p>
                    </template>
                </m-faq>
            </div>

            <div v-if="is_second_generation_shipping_message">
                <m-faq>
                    <template v-slot:question>Which integrations can trigger this message?</template>
                    <template v-slot:answer>
                        This message can<sup>*</sup> be triggered by the following integrations:
                        <m-integrations-that-support
                            :concept="shipping_trigger_concept"
                        />
                        <p
                            style="margin-top: 10px"
                            v-if="template.template_context == 'shipping-new-tracking-number'"
                        >
                            *  For an integration to trigger this message you must (1) be entering the tracking number of shipments into the integration and (2) the integration has to know the telephone number(s) of the package's recipient(s).
                        </p>
                        <p
                            style="margin-top: 10px"
                            v-else
                        >
                            *  For an integration to trigger this message it must be tracking the package in question.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>What if I have multiple integrations turned on &mdash; will my customers receive duplicate shipping notifications?</template>
                    <template v-slot:answer>
                        <p>
                            RedChirp automatically de-duplicates duplicate messages.  As a result, your customers <b>will not</b> receive duplicate messages about the same package, no matter how many integrations you have enabled.
                        </p>
                        <p>
                            <b>Note:</b> Some shipping events (e.g. out for delivery, delivery attempt failed, etc.) can occur multiple times in the course of delivering one packag.
                            For example, the same package may go out for delivery on one day, have the delivery attempt fail, and then go out for delivery again the next day.
                            In these cases, RedChirp will send automated message only one time per package per phone number each day.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>What about old events &mdash; will RedChirp sent messages about packages shipped a long time ago?</template>
                    <template v-slot:answer>
                        <p>
                            RedChirp will only send shipping related messages where the time of the event is today or yesterday.
                            Older events will simply be ignored. See the FAQ "Where does RedChirp read the scheduled ship date from?" to see where RedChirp gets this date from each integration source.
                        </p>
                        <p>
                            If RedChirp is aware of an event that generally happens later in the lifecycle of a shipment &mdash; First In Transit Event, Out for Delivery, Delivery Attempt Failed, Delivered, Available for Pickup, or Picked up &mdash; at the time it is preparing this message, RedChirp will not send this message.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>What phone numbers will receive this message?</template>
                    <template v-slot:answer>
                        <p>
                            RedChirp will send this text to every phone number that appears anywhere on the order (provided, of course, that the number has not opted out of texting).
                            If you do not want a specific phone number to receive these text messages — for example, the phone number of a corporate gifter who is included in many orders — use the
                            <a href="https://redchirp.com/how-to-exclude-recipients-from-your-automated-messages/" target="_blank">Excluded Recipients functionality.</a>
                        </p>
                        <p>
                            Every phone number that appears anywhere on the order, provided, of course, that the number has not opted out of texting.
                        </p>
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>What happens when a shipment contains more than one package?</template>
                    <template v-slot:answer>
                        <p>
                            RedChirp's automated shipment messages are per tracking number so customers will receive a text message <i>per package</i>.
                        </p>
                        <p>
                            Each package is treated individually by design because packages can diverge from one another.
                        </p>
                    </template>
                </m-faq>

                <div
                    v-if="template.template_context == 'shipping-new-tracking-number'"
                >
                     <m-faq>
                        <template v-slot:question>Where does RedChirp read the scheduled ship date from?</template>
                        <template v-slot:answer>
                            <ul>
                            <li>Commerce7</li>
                            <ul>
                                <li>
                                    RedChirp reads the date that you select as the "Fulfillment Date" in the create fulfillment screen in Commerce7 (<a href="/app/help-images/c7-ship-date-1.png" target="_blank">picture</a>).
                                </li>
                                <li>
                                    For orders that are shipped: after being entered this date shows on the Fulfillment tab as the "Shipped on" date. (<a href="/app/help-images/c7-ship-date-2.png" target="_blank">picture</a>)
                                </li>
                            </ul>
                            <li>eCELLAR</li>
                            <ul>
                                <li>
                                    RedChirp reads the date that you enter as the date shipped
                                </li>
                            </ul>
                            <li>OrderPort</li>
                            <ul>
                                <li>
                                    RedChirp reads the date that you enter as the ship date
                                </li>
                            </ul>
                            <li>ShipCompliant</li>
                            <ul>
                                <li>
                                    RedChirp reads the "Actual Ship Date" field from ShipCompliant (<a href="/app/help-images/ship-compliant-ship-date.png" target="_blank">picture</a>)
                                </li>
                            </ul>
                            <li>ShipEngine</li>
                            <ul>
                                <li>
                                    RedChirp reads the carrier's actual label created time
                                </li>
                            </ul>
                            <li>WineCo</li>
                            <ul>
                                <li>
                                    RedChirp reads the carrier's actual label created time
                                </li>
                            </ul>
                            <li>WineDirect</li>
                            <ul>
                                <li>
                                    RedChirp reads the Shipped Date field from WineDirect
                                </li>
                            </ul>

                            <li>WineDirect Fulfillment</li>
                            <ul>
                                <li>
                                    RedChirp reads the carrier's actual label created time
                                </li>
                            </ul>
                            <li>Wineshipping</li>
                            <ul>
                                <li>
                                    If the "Requested Ship Date (RSD)" is set <span class="text-decoration-underline">and</span> in the future, then RedChirp uses the "Requested Ship Date"
                                </li>
                                <li>
                                    Otherwise, RedChirp reads the carrier's actual label created time
                                </li>
                            </ul>
                        </ul>
                        </template>
                    </m-faq>
                </div>
            </div>

            <div v-if="template.template_context == 'order-placed-request-opt-in'">
                <m-faq>
                    <template v-slot:question>When will this message be sent?</template>
                    <template v-slot:answer>
                        RedChirp will use smart routing when both "Order Placed" and "Order Placed: Request Opt In" messages are enabled:
                        <ul>
                            <li>If the recipient has promotional consent on file, the regular "Order Placed" message will typically be sent instead</li>
                            <li>If the recipient does not have promotional consent, this "Request Opt In" message will typically be sent</li>
                        </ul>
                        RedChirp will try to send this message:
                        <ul>
                            <li>After RedChirp syncs a new order from your connected CRM/POS</li>
                            <li>Only when the customer does not already have promotional consent on file in RedChirp</li>
                            <li>Only for orders with a total of at least $1.00</li>
                            <li>At most once per week per phone number (weekly throttling)</li>
                        </ul>
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>Who will receive this message?</template>
                    <template v-slot:answer>
                        All customer phone numbers associated with the order. Use conditions to further target (e.g., POS vs. online orders).
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>Why didn’t a message send for a small order?</template>
                    <template v-slot:answer>
                        Orders under $1.00 (including $0 and negative/refund totals) do not trigger this message.
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>How often can this message send to a customer?</template>
                    <template v-slot:answer>
                        To reduce noise, RedChirp limits this message to once per week per phone number.
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>Can I send different content for POS vs. other order types?</template>
                    <template v-slot:answer>
                        Yes. Add a condition for order type (e.g., POS) with its own message content. Orders not matching conditions will use the default message content.
                    </template>
                </m-faq>
            </div>

            <div v-if="template.template_context == 'order-placed'">
                <m-faq>
                    <template v-slot:question>When will this message be sent?</template>
                    <template v-slot:answer>
                        RedChirp will use smart routing when both "Order Placed" and "Order Placed: Request Opt In" messages are enabled:
                        <ul>
                            <li>If the recipient has promotional consent on file, this "Order Placed" message will typically be sent</li>
                            <li>If the recipient does not have promotional consent, the "Order Placed: Request Opt In" message will typically be sent instead</li>
                        </ul>
                        RedChirp will try to send this message:
                        <ul>
                            <li>After RedChirp syncs a new order from your connected CRM/POS</li>
                            <li>For all order types (including club orders, if not filtered)</li>
                            <li>When smart routing is active, generally only to recipients with promotional consent on file</li>
                        </ul>
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>How does smart routing work with the "Order Placed: Request Opt In" message?</template>
                    <template v-slot:answer>
                        When both "Order Placed" and "Order Placed: Request Opt In" messages are enabled, RedChirp will automatically route customers based on their promotional consent status:
                        <ul>
                            <li>Customers with promotional consent will typically receive the regular "Order Placed" confirmation message</li>
                            <li>Customers without promotional consent will typically receive the "Order Placed: Request Opt In" message</li>
                            <li>This approach is designed to help ensure all customers receive appropriate communication while respecting consent preferences</li>
                            <li>The routing decision is logged in the event log for transparency</li>
                        </ul>
                    </template>
                </m-faq>

                <m-faq>
                    <template v-slot:question>Who will receive this message?</template>
                    <template v-slot:answer>
                        All customer phone numbers associated with the order. When smart routing is active, generally only customers with promotional consent on file will receive this message. Use conditions to further target (e.g., POS vs. online orders).
                    </template>
                </m-faq>
            </div>

            <div v-if="template.template_context == 'order-fulfilled-follow-up'">
                <m-faq>
                    <template v-slot:question>Which integrations can trigger this message?</template>
                    <template v-slot:answer>
                        This message can be triggered by the following integrations:
                        <m-integrations-that-support
                            concept="can-trigger-order-follow-up"
                        />
                    </template>
                </m-faq>
                <m-faq>
                    <template v-slot:question>Commerce7: what precisely determines if/when this message will be sent?</template>
                    <template v-slot:answer>
                        <ul>
                            <li>
                                This message will be sent the specified number of days after the "Shipped Date" of the order
                            </li>
                            <li>
                                This message will not be sent if it has been cancelled before the indended follow up date.
                            </li>
                        </ul>
                    </template>
                </m-faq>
                <m-faq>
                    <template v-slot:question>OrderPort: what precisely determines if/when this message will be sent?</template>
                    <template v-slot:answer>
                        <ul>
                            <li>
                                This message will be sent the specified number of days after the "Shipped Date" of the order
                            </li>
                            <li>
                                This message will not be sent if it has been cancelled before the indended follow up date.
                            </li>
                        </ul>
                    </template>
                </m-faq>
                <m-faq>
                    <template v-slot:question>WineDirect: what precisely determines if/when this message will be sent?</template>
                    <template v-slot:answer>
                        <ul>
                            <li>
                                This message will be sent the specified number of days after the <i>first</i> of either the "Shipped Date" or the "Pickup Date"
                            </li>
                            <li>
                                This message will not be sent if it has been cancelled before the indended follow up date.
                            </li>
                        </ul>
                    </template>
                </m-faq>
            </div>
        </div>
    </div>

    <m-edit-text
        v-if="template.template_context == 'wine-club-join-follow-up'"
        label="Delay (days between join date and this message being sent)"
        v-model="template.param_delay_days"
        v-on:input="save"
        integer_positive
        :read_only="form_is_readonly"
    />

    <m-edit-text
        v-if="template.template_context == 'order-fulfilled-follow-up'"
        label="Delay (days between the earliest fulfillment on this order and this message being sent)"
        v-model="template.param_delay_days"
        v-on:input="save"
        integer_positive_non_zero
        :read_only="form_is_readonly"
    />

    <!-- Data Cleaning Certification Status -->
    <div v-if="data_cleaning_certification_status !== null" >
        <!-- Warning when not certified -->
        <v-alert
            v-if="!data_cleaning_certification_status"
            border="left"
            colored-border
            color="primary"
            elevation="2"
            type="warning"
            class="mb-4"
        >
            <div class="d-flex align-center">
                <div class="flex-grow-1">
                    <p class="mb-2">
                        <strong>Certification Required:</strong> Birthday messages will not be sent until you certify your data cleaning rules.
                    </p>
                    <p class="mb-0">
                        These rules ensure that birthday messages are not sent to customers that may have placeholder dates input as their birthday.
                    </p>
                </div>
                <div class="ml-4">
                    <v-btn
                        color="primary"
                        @click="onClickDataCleaningSettings"
                        small
                    >
                        <v-icon left small>mdi-alert</v-icon>
                        Certify Data Cleaning Rules
                    </v-btn>
                </div>
            </div>
        </v-alert>

        <!-- Info when certified (less obtrusive) -->
        <v-alert
            v-else
            border="left"
            colored-border
            color="success"
            elevation="1"
            type="success"
            class="mb-4"
            dense
        >
            <div class="d-flex align-center">
                <div class="flex-grow-1">
                    <span class="text-body-2">
                        Data cleaning rules configured and certified for birthday data
                    </span>
                </div>
                <div class="ml-3">
                    <v-btn
                        @click="onClickDataCleaningSettings"
                        outlined
                        mall
                        class="text-caption"
                    >
                        <v-icon left small>mdi-cog</v-icon>
                        Configure Rules
                    </v-btn>
                </div>
            </div>
        </v-alert>
    </div>

   <v-text-field
        label="Organization"
        :value="organization_label"
        readonly
        disabled
    />

    <v-text-field
        label="Template Type"
        :value="template.template_context"
        readonly
        disabled
    />

    <v-select
        label="Message Type"
        :items="filteredMessageTypeChoices"
        v-model="template.message_type"
        v-on:change="save"
        v-if="template.template_context == 'birthday-message' || template.template_context == 'birthday-month-message'"
        :disabled="form_is_readonly"
    />

     <m-edit-text
        label="Time to wait after a member is added to a list before sending"
        v-model="template.list_drip_delay"
        duration
        v-on:input="save"
        v-if="template.list_drip_list_id != null"
        :read_only="form_is_readonly"
    />

    <m-edit-text
        label="Template Label"
        v-model="template.template_label"
        v-on:input="save"
        autocomplete="off"
        :read_only="form_is_readonly"
    />

    <div
        v-if="template.template_context == 'birthday-message' || template.template_context == 'birthday-month-message'"
    >
        <m-edit-text
            v-if="template.template_context == 'birthday-month-message'"
            label="Day of month to send (1-27)"
            hint="This message will only attempt to send on this day of the month"
            v-model="template.param_delay_days"
            integer_positive
            :rules="[validateDayOfMonthForBirthdayMonth]"
            v-on:input="save"
            style="margin-bottom: 15px"
            :read_only="form_is_readonly"
        />
        <m-edit-text
            v-if="template.template_context == 'birthday-message'"
            hint="Positive value: Sends the message that many days AFTER the birthday. Negative value: Sends the message that many days BEFORE the birthday. Zero: Sends the message ON the birthday. "
            label="Day to send relative to birthday"
            v-model="template.param_delay_days"
            v-on:input="save"
            integer
            style="margin-bottom: 5px"
            :read_only="form_is_readonly"
        />
        <div
            v-if="template.template_context == 'birthday-message'"
            style="margin-bottom: 15px; margin-left: 15px; color: rgba(0,0,0,0.6); font-size: 14px;"
        >
            <span v-if="template.param_delay_days == 0">
                <i>This message will be sent <b>on the day of the birthday</b>.</i>
            </span>
            <span v-else-if="template.param_delay_days == 1">
                <i>This message will be sent <b>1 day after the birthday</b>.</i>
            </span>
            <span v-else-if="template.param_delay_days > 1">
                <i>This message will be sent <b>{{ template.param_delay_days }} days after the birthday</b>.</i>
            </span>
            <span v-else-if="template.param_delay_days == -1">
                <i>This message will be sent <b>1 day before the birthday</b>.</i>
            </span>
            <span v-else>
                <i>This message will be sent <b>{{ Math.abs(template.param_delay_days) }} days before the birthday</b>.</i>
            </span>
        </div>





    </div>

    <div
        v-if="template.template_context == 'mailchimp'"
    >
        <v-data-table
            :headers="mailchimp_campaign_headers"
            :items="mailchimp_campaigns"
            :items-per-page="100"
            class="elevation-1"
            :hide-default-footer="true"
            disable-sort
            :footer-props="{
                disableItemsPerPage: false,
                disablePagination: false,
            }"
            no-data-text="You have not selected any mailchimp campaigns.  Use the (+) button below to add a mailchimp campaign"
        >

            <template v-slot:item.action="{ item }">
                <v-menu
                    bottom
                    offset-y
                >

                <template v-slot:activator="{ on }">

                    <v-icon
                        v-on="on"
                    >
                        mdi-dots-vertical
                    </v-icon>

                </template>

                <v-list>
                    <v-list-item
                        v-on:click="onClickRemoveCRMEmailCampaign(item.value)"
                        :disabled="form_is_readonly"
                    >
                        <v-list-item-title>Remove</v-list-item-title>
                    </v-list-item>

                </v-list>

                </v-menu>
            </template>

        </v-data-table>

        <m-icon-bar
            v-if="!form_is_readonly"
            v-on:add="onClickAddMailchimpCampaign"
        />

        <m-auto-message-add-crm-email-dailog
            ref="auto-message-add-crm-email-dailog"
            content_type="mailchimp"
            v-on:input="onAddCRMEmailCampaignId"
        />

        <v-select
            label="Mailchimp Event (Type) (Required)"
            :items="$rc.enumChoices('crm_email_event_type',false)"
            multiple
            v-model="template.email_event_triggers"
            v-on:change="save"
            :disabled="form_is_readonly"
        />

        <p><b>Note</b> Mailchimp does not always record and report events in real time.  Delays of up to an hour from the event to the report have been observed.  Average delay observed is 14 minutes 17 seconds.</p>
    </div>

    <div
        style="text-align: right"
        v-if="supports_conditions"
    >
        <v-btn
            color="primary"
            :disabled="form_is_readonly"
            v-on:click="onClickAddConditionMessage"
            style="margin-bottom: 15px"
        >
            Add Conditional Message...
        </v-btn>
    </div>

    <m-auto-message-condition-edit-dialog
        ref="condition-edit-dialog"
        v-on:save="onEditConditionSave"
    />

    <v-alert
      v-if="template.conditions.length > 1"
      border="left"
      colored-border
      color="primary"
      elevation="2"
      type="info"
      dismissible
    >
      <b>Note:</b> Conditions are evaluated in order (i.e. the first condition that matches is the message that is sent) -- therefore, the order of your conditions matters.<br/><br/>You can move conditions up and down using the "move up" and "move down" buttons respectively.
   </v-alert>



    <v-expansion-panels
        v-if="template.conditions.length > 0"
        style="margin-bottom: 30px; margin-top: 15px;"
    >
        <v-expansion-panel
            v-for="(conditional, index) in template.conditions"
            :key="'panel-'+index"
        >
            <v-expansion-panel-header><b><span v-html="conditional.description"/></b></v-expansion-panel-header>

            <v-expansion-panel-content>

                <m-auto-message-message-ctrls
                    :send_from_choices="send_from_choices"
                    :link_shortener_host_choices="link_shortener_host_choices"
                    :message_pattern_context="template.template_context"
                    :value="conditional"
                    v-on:input="onChangeConditionMessageCtrls"
                    :disable_mode="organization_account != null && organization_account.is_sandbox_organization"
                    :read_only="form_is_readonly"
                />

                <div
                    style="text-align: right; margin-top: 5px;"
                >
                    <v-btn
                        v-if="conditional.condition_index > 0"
                        :disabled="form_is_readonly"
                        style="margin-right: 15px;"
                        v-on:click="onClickMoveConditionalUp(conditional)"
                    >
                        Move Up...
                    </v-btn>

                    <v-btn
                        v-if="conditional.condition_index+1 <= template.conditions.length-1"
                        :disabled="form_is_readonly"
                        style="margin-right: 15px;"
                        v-on:click="onClickMoveConditionalDown(conditional)"
                    >
                        Move Down...
                    </v-btn>

                    <v-btn
                        :disabled="form_is_readonly"
                        style="margin-right: 15px;"
                        v-on:click="onClickDeleteConditional(conditional)"
                    >
                        Delete this conditional message...
                    </v-btn>

                    <v-btn
                        :disabled="form_is_readonly"
                        v-on:click="onClickEditConditions(conditional)"
                    >
                        Edit Conditions...
                    </v-btn>
                </div>

            </v-expansion-panel-content>

        </v-expansion-panel>

        <v-expansion-panel>
            <v-expansion-panel-header><b>Otherwise (i.e. if none of the above conditions match)</b></v-expansion-panel-header>
            <v-expansion-panel-content>
                <m-auto-message-message-ctrls
                    :send_from_choices="send_from_choices"
                    :link_shortener_host_choices="link_shortener_host_choices"
                    :message_pattern_context="template.template_context"
                    :value="template"
                    v-on:input="onChangeDefaultMessageCtrls"
                    :disable_mode="organization_account != null && organization_account.is_sandbox_organization"
                    :read_only="form_is_readonly"
                    default_message_control
                />
            </v-expansion-panel-content>
        </v-expansion-panel>
    </v-expansion-panels>
    <div
        v-else
    >
        <m-auto-message-message-ctrls
                    :send_from_choices="send_from_choices"
                    :link_shortener_host_choices="link_shortener_host_choices"
                    :message_pattern_context="template.template_context"
                    :value="template"
                    v-on:input="onChangeDefaultMessageCtrls"
                    :disable_mode="organization_account != null && organization_account.is_sandbox_organization"
                    :read_only="form_is_readonly"
                    default_message_control
                />
    </div>



    <p style="margin-top: 15px;"><b>Send During Hours ({{send_during_timezone}})</b></p>
    <m-daily-schedule
        v-model="template.send_during"
        :read_only="form_is_readonly"
        v-on:input="save"
    />


    <div
        v-if="template.template_context == 'klaviyo'"
    >

       <b>Klaviyo Instructions</b>

       <m-display-text
            label="URL"
            :value="klaviyo_webhook_url"
            style="margin-top: 15px"
        />


        JSON Body
         <m-code-block
            :click_anywhere_to_copy="false"
        >
            {<br/>
                "red_chirp_template_subject_id" : "{{template.template_subject_id}}",<br/>
                "first_name" : "<span v-pre>{{ person.first_name }}</span>",<br/>
                "last_name" : "<span v-pre>{{ person.last_name }}</span>",<br/>
                "email" : "<span v-pre>{{ person.email }}</span>",<br/>
                "phone_number" : "<span v-pre>{{ person.phone_number }}</span>"<br/>
            }<br/>
        </m-code-block>

        <p>
            For details on how to add a webhook to a flow in Klaviyo <a href="https://developers.klaviyo.com/en/docs/how-to-add-a-webhook-action-to-a-flow" target="_blank">click here</a>.
        </p>

        <p>
            <b>Note:</b> RedChirp will, to the best of its ability, automatically populate missing customer profile fields.
            For exampe, if a Klaviyo customer profile has email address but is missing a phone number, but you have a RedChirp contact that has the same email address, then that RedChirp contact will be automatically used to fill in the phone number.  If a contact has multiple phone numbers associated with it, and Klaviyo does not specify the phone number to text, then <b>all</b> phone numbers associated with the contact will be texted (provided they are SMS capable).
        </p>



    </div>

    <div
        v-if="template.template_context == 'custom_automated_message'"
    >

       <b>Custom Automated Message Instructions</b>

       <m-display-text
            label="URL"
            :value="custom_webhook_url"
            style="margin-top: 15px"
        />


        JSON Body Shape
         <m-code-block
            :click_anywhere_to_copy="false"
        >
            {<br/>
                "first_name" : "John",<br/>
                "last_name" : "Doe",<br/>
                "to" : "237-111-2345",<br/>
                "custom-semantic-meaning": "1234"<br/>
                // Optional: any custom fields you would like to use in your message<br/>
            }<br/>
        </m-code-block>

        <p>
            For details on how to trigger a custom automated message <a href="https://redchirp.com/how-to-setup-custom-automated-messages/" target="_blank">click here</a>.
        </p>



    </div>

    <b>Excluded Recipients</b>
    <p>You have excluded {{count_excluded_endpoints}} from this automated message.</p>
    <div
        style="text-align: right"
    >
        <v-btn
            v-on:click="onClickEditExcludedRecipients"
            :disabled="form_is_readonly"
        >
            Edit Excluded Recipients...
        </v-btn>
    </div>

    <b>Consent</b>
    <v-checkbox
        label="Only send to recipients that have informational consent on file in RedChirp"
        v-model="template.require_grant_consent_information"
        v-on:change="doSave"
        style="margin-bottom: -25px"
        :disabled="form_is_readonly"
    />

    <v-checkbox
        label="Only send to recipients that have promotional consent on file in RedChirp"
        v-model="template.require_grant_consent_promotion"
        v-on:change="doSave"
        :disabled="form_is_readonly"
    />

    <m-edit-text
        label="Only send to recipients that have not been sent this message in the specified number of days"
        v-model="template.filter_days_since_received_this_message"
        v-on:input="doSave"
        integer_positive
        :read_only="form_is_readonly"
    />

</v-container>
</m-pageframe>
</template>

<script>
const _ = require('lodash');
const Time = require('@/js/time.js').default;
const Validate = require('@/js/validate.js').default;


export default {


    data() {
        return {
            organization_label: null,
            template: null,

            send_from_choices: [],
            send_during_timezone: null,
            supports_conditions: false,
            condition_profiles: [],
            link_shortener_hosts: [],
            organization_account: null,
            count_excluded_endpoints: 0,
            integration_crm_summaries: [],
            data_cleaning_certification_status: null,
            certification_poll_interval: null,
            show_red_chirp_staff_no_edit_in_auto_mode: false,

            mailchimp_campaign_headers: [
                { text: 'Campaign Id', value: 'value'},
                { text: 'Mailchimp Campaign', value: 'text'},
                { text: 'Action', value: 'action'},
            ],

            upgradeable_to_message_pattern_context: null,

            // Add Time and Validate to make them available in the template
            Time: Time,
            Validate: Validate,
        }
    },

    mounted () {
        this.update();
    },

    beforeDestroy() {
        // Clean up polling interval to prevent memory leaks
        this.stopCertificationPolling();
    },

    methods: {


        update() {

            let params = {
                'organization_id' : this.$route.params.organization_id,
                'template_subject_id' : this.$route.params.template_subject_id,
                'template_context' : this.$route.params.template_context,
            };

            this.$rc.showSpinner();

            this.$rc.getAutoMessageTemplateProfile(
                params,

                (data) => {
                    this.$rc.hideSpinner();

                    this.show_red_chirp_staff_no_edit_in_auto_mode = data.show_red_chirp_staff_no_edit_in_auto_mode;
                    this.organization_label = data.organization_label;
                    this.template = data.template;
                    this.send_from_choices = data.send_from_choices;
                    this.send_during_timezone = data.send_during_timezone;
                    this.supports_conditions = data.supports_conditions;

                    // Load data cleaning certification status for birthday templates
                    this.loadDataCleaningCertificationStatus();
                    this.condition_profiles = data.condition_profiles;
                    this.integration_crm_summaries = data.integration_crm_summaries;
                    this.link_shortener_hosts = data.link_shortener_hosts;
                    this.organization_account = data.organization_account;
                    this.count_excluded_endpoints = data.count_excluded_endpoints;
                    this.upgradeable_to_message_pattern_context = data.upgradeable_to_message_pattern_context;

                    this.numberConditions();
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to complete API call');
                },
            );
        },

        save: _.debounce(function(){
            this.doSave();
        }, 1000),

        doSave()
        {
            // just in case bad actor got around the readonly inputs or an individual input is not set up properly in the future
            if (this.form_is_readonly) {
                const error_message = this.show_red_chirp_staff_no_edit_in_auto_mode ?
                    'RedChirp staff may not edit an automated message that is in automatic mode' :
                    'Editing is disabled for this message';

                this.$rc.alert('Error', error_message);
                this.update();
                return;
            }

            // Create a copy of the template for saving
            let template_to_save = { ...this.template };

            // For non-birthday message types, remove the message_type field to leave it unset/null
            if (template_to_save.template_context !== 'birthday-message' &&
                template_to_save.template_context !== 'birthday-month-message') {
                delete template_to_save.message_type;
            }

            this.$rc.putAutoMessageTemplate(
                template_to_save,

                (data) => {
                    this.template = data.template;
                    this.numberConditions();
                },

                () => {
                    this.$rc.alert('Error','Unable to save template');
                },
            );
        },

        onChangeDefaultMessageCtrls(new_value)
        {
            this.updateTemplateFromMessageCtrls(new_value);
        },

        updateTemplateFromMessageCtrls(new_value)
        {
            this.template.send_mode = new_value.send_mode;
            this.template.send_from = new_value.send_from;
            this.template.link_shortener_host = new_value.link_shortener_host;
            this.template.message_patterns = new_value.message_patterns;
            this.template.blob_ids = new_value.blob_ids;
            this.template.vcard_id = new_value.vcard_id;
            this.template.time_delay_send_after_enqueue = new_value.time_delay_send_after_enqueue;
            this.template.time_delay_expire_after_trigger = new_value.time_delay_expire_after_trigger;

            this.save();
        },

        checkDataCleaningCertification(callback)
        {
            this.$rc.rest({
                'endpoint': 'GET: /api/v1/organization-data-cleaning-rules/:organization_id',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                },
                'on_success': (data) => {
                    // Check the is_certified field from the data cleaning rules
                    callback(data.is_certified === true);
                },
                'on_error': () => {
                    // If no rules exist yet, they're not certified
                    callback(false);
                }
            });
        },

        onClickPlaceIntoManualMode()
        {
            this.$rc.confirm({
                'title': 'Put Into Manual Mode',
                'body': 'This message will be placed into manual mode (including all conditional messages).  Proceed?',
                'btn_text_ok': 'Proceed: Place Into Manual Mode',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doPlaceIntoManualMode();
                },
            });
        },

        doPlaceIntoManualMode()
        {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/auto-messages/templates/put-into-manual-mode',
            
                'params' : {
                        'organization_id': this.$route.params.organization_id,
                        'template_context': this.$route.params.template_context,
                        'template_subject_id': this.$route.params.template_subject_id
                    },
            
                'on_success': (data) => {
                        this.update();
                    },
            
                'on_error': 'Unable to place message into manual mode',
            });
        },

        loadDataCleaningCertificationStatus()
        {
            // Only load for birthday message templates
            if (this.template && (this.template.template_context === 'birthday-message' || this.template.template_context === 'birthday-month-message')) {
                this.checkDataCleaningCertification((is_certified) => {
                    this.data_cleaning_certification_status = is_certified;
                });
            }
        },

        onChangeConditionMessageCtrls(new_value)
        {
            let index = new_value.condition_index;

            this.template.conditions[index].send_mode = new_value.send_mode;
            this.template.conditions[index].send_from = new_value.send_from;
            this.template.conditions[index].link_shortener_host = new_value.link_shortener_host;
            this.template.conditions[index].message_patterns = new_value.message_patterns;
            this.template.conditions[index].custom_time_delay_send_after_enqueue = new_value.custom_time_delay_send_after_enqueue;
            this.template.conditions[index].custom_time_delay_expire_after_trigger = new_value.custom_time_delay_expire_after_trigger;

            this.save();
        },

        onClickAddConditionMessage()
        {
            let condition = {
                'label' : 'Conditional message',
                'constraints' : [],
                'send_mode' : 'manual',
                'send_from' : this.template.send_from,
                'message_patterns' : this.template.message_patterns,
                'condition_index': -1,
            };

            this.$refs['condition-edit-dialog'].showDialog(condition, this.condition_profiles, this.integration_crm_summaries);
        },

        onEditConditionSave(condition)
        {
            let index = condition.condition_index;

            if ( index == -1 )
            {
                this.template.conditions.push(condition);
                this.numberConditions();
                this.save();
                return;
            }

            this.template.conditions[index] = condition;
            this.$forceUpdate();
            this.save();
        },

        getSelectedConstraintValue(conditional,type)
        {
            let constraints = conditional.constraints;

            for ( let i = 0; i < constraints.length; i++ )
            {
                if ( constraints[i].type == type )
                    return constraints[i].values;
            }

            return [];
        },

        onClickEditConditions(condition)
        {
            this.$refs['condition-edit-dialog'].showDialog(condition, this.condition_profiles, this.integration_crm_summaries);
        },

        onClickDeleteConditional(condition)
        {
            this.$rc.confirm(
                'Delete condition',
                'Permenantly delete this conditional message?',
                [
                    {'text': 'Cancel', 'value': 'cancel'},
                    {'text': 'Delete', 'value': 'confirm'},
                ],

                (response) => {
                    if (response != 'confirm') return;

                    let index = condition.condition_index;
                    this.template.conditions.splice(index,1);
                    this.$forceUpdate();
                    this.save();
                }
            );
        },

        onClickMoveConditionalUp(condition)
        {
            let index = condition.condition_index;
            if ( index <= 0 ) return;


            let tmp = this.template.conditions[index-1];
            this.template.conditions[index-1] = this.template.conditions[index];
            this.template.conditions[index] = tmp;
            this.numberConditions();

            this.$forceUpdate();
            this.save();
        },

        onClickMoveConditionalDown(condition)
        {
            let index = condition.condition_index;
            if ( !(index+1 <= this.template.conditions.length-1) ) return;

            let tmp = this.template.conditions[index+1];
            this.template.conditions[index+1] = this.template.conditions[index];
            this.template.conditions[index] = tmp;
            this.numberConditions();

            this.$forceUpdate();
            this.save();
        },

        numberConditions()
        {
            for ( let i = 0; i < this.template.conditions.length; i++ )
            {
                        this.template.conditions[i].condition_index = i;
            }
        },

        onClickAddMailchimpCampaign()
        {
            this.$refs['auto-message-add-crm-email-dailog'].showDialog();
        },

        onAddCRMEmailCampaignId(email_campaign_id)
        {
            if ( this.template.template_context == 'mailchimp' ) this.template.email_content_type = 'mailchimp';

            this.template.email_campaign_ids.push(email_campaign_id);
            this.doSave();
        },

        onClickRemoveCRMEmailCampaign(email_campaign_id)
        {
            if ( this.template.template_context == 'mailchimp' ) this.template.email_content_type = 'mailchimp';

            for ( let i = this.template.email_campaign_ids.length-1; i >= 0; i-- )
            {
                if ( this.template.email_campaign_ids[i] == email_campaign_id )
                    this.template.email_campaign_ids.splice(i,1);
            }

            this.doSave();
        },

        onClickEditExcludedRecipients()
        {
            this.$router.push({
                'name': 'auto-messages-excluded-recipients',
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'template_context': this.$route.params.template_context,
                    'template_subject_id': this.$route.params.template_subject_id
                }
            });
        },


        validateDayOfMonthForBirthdayMonth(value)
        {
            if (value === null || value === undefined || value === '') return true;

            const day = parseInt(value);
            if (isNaN(day)) return 'Please enter a valid number';
            if (day < 1 || day > 27) return 'Day of month must be between 1 and 27';

            return true;
        },



        onClickBackToAutoMessages() {
            // Use router.replace with a timestamp query parameter to force refresh
            // and prevent cached versions from being shown
            this.$router.replace({
                'name': 'auto-messages-settings',
                'params': {
                    'organization_id': this.$route.params.organization_id
                },
                'query': {
                    '_refresh': Date.now() // Force refresh by adding timestamp
                }
            });
        },



        onClickDataCleaningSettings() {
            // Open data cleaning settings in a new tab
            const route = this.$router.resolve({
                'name': 'settings-organization-data-cleaning-rules',
                'params': {
                    'organization_id': this.$route.params.organization_id
                }
            });
            window.open(route.href, '_blank');

            // Start polling for certification status updates if not currently certified
            if (this.data_cleaning_certification_status === false || this.data_cleaning_certification_status === null) {
                this.startCertificationPolling();
            }
        },

        startCertificationPolling() {
            // Don't start polling if already polling
            if (this.certification_poll_interval) {
                return;
            }


            // Poll every 3 seconds for certification status updates
            this.certification_poll_interval = setInterval(() => {
                this.pollCertificationStatus();
            }, 3000);

            // Stop polling after 5 minutes to avoid infinite polling
            setTimeout(() => {
                this.stopCertificationPolling();
            }, 5 * 60 * 1000);
        },

        stopCertificationPolling() {
            if (this.certification_poll_interval) {
                //console.log('Stopping certification status polling');
                clearInterval(this.certification_poll_interval);
                this.certification_poll_interval = null;
            }
        },

        pollCertificationStatus() {
            // Only poll for birthday message templates
            if (this.template && (this.template.template_context === 'birthday-message' || this.template.template_context === 'birthday-month-message')) {
                this.checkDataCleaningCertification((is_certified) => {
                    const previous_status = this.data_cleaning_certification_status;
                    this.data_cleaning_certification_status = is_certified;

                    // If certification status changed from false/null to true, stop polling
                    if ((previous_status === false || previous_status === null) && is_certified === true) {
                        //console.log('Certification detected! Stopping poll.');
                        this.stopCertificationPolling();

                    }
                });
            }
        },
    },

    computed: {
        form_is_readonly() {
            return !!this.show_red_chirp_staff_no_edit_in_auto_mode;
        },

        filteredMessageTypeChoices() {
            // Filter out the "p2p" option from the async_message_type enum
            return this.$rc.enumChoices('async_message_type', false)
                .filter(choice => choice.value !== 'p2p');
        },

        title() {
            let ol = this.organization_label;
            let template = this.template;

            if ( ol == null || template == null )
                return "Template Settings";

            return 'Settings: '+template.template_label;
        },

        commerce7_email_when_will_it_send()
        {
            if ( this.template == null ) return null;

            let template = this.template;

            if ( template.template_context == 'commerce-7-transaction-email-order-confirmation-club-ship' ) return { 'label': 'Order Confirmation Club Ship', 'fourteen_day_limit': false };
            if ( template.template_context == 'commerce-7-transaction-email-order-confirmation-ship' ) return { 'label': 'Order Confirmation Ship', 'fourteen_day_limit': false };
            if ( template.template_context == 'commerce-7-transaction-email-club-ship-two-weeks' ) return { 'label': 'Club Shipment 2 Weeks Away', 'fourteen_day_limit': false };
            if ( template.template_context == 'commerce-7-transaction-email-club-ship-two-days' ) return { 'label': 'Club Shipment 2 Days Away', 'fourteen_day_limit': false };
            if ( template.template_context == 'commerce-7-transaction-email-club-credit-card-decline' ) return { 'label': 'Club Member Shipment Credit Card Decline', 'fourteen_day_limit': true };
            if ( template.template_context == 'commerce-7-transaction-email-order-club-pickup' ) return { 'label': 'Order Confirmation Club Pickup', 'fourteen_day_limit': false };
            if ( template.template_context == 'commerce-7-transaction-email-account-activation' ) return { 'label': 'Account Confirmation', 'fourteen_day_limit': false };

            return null;
        },

        link_shortener_host_choices()
        {
            let ret = [];

            ret.push({ 'text' : 'Use default link shortener', 'value' : null});
            ret.push({ 'text': 'Do not shorten links', 'value': 'do-no-shorten-link-under-any-circumstances.biz'});

            let hosts = this.link_shortener_hosts;
            if ( hosts == null ) return ret;

            for ( let i = 0; i < hosts.length; i++ )
            {
                if ( hosts[i].is_active == false ) continue;

                ret.push({ 'text' : hosts[i].host, 'value' : hosts[i].host});
            }

            return ret;
        },

        klaviyo_webhook_url()
        {
            let organization_id = this.$route.params.organization_id;
            return 'https://app.redchirp.com/api/v1/webhook/inbound/klaviyo/'+organization_id;
        },

        custom_webhook_url()
        {
            return 'https://app.redchirp.com/api/v1/external/automated-messages/trigger/'+this.template.template_subject_id;
        },

        mailchimp_campaigns()
        {
            if ( this.template == null ||  this.template.email_campaign_ids == null || this.template.email_campaign_labels == null || this.template.email_campaign_ids.length != this.template.email_campaign_labels.length )
                return [];

            let ret = [];

            for ( let i = 0; i < this.template.email_campaign_ids.length; i++ )
            {

                ret.push({
                    'text' : this.template.email_campaign_labels[i],
                    'value' : this.template.email_campaign_ids[i],
                });
            }

            return ret;
        },

        is_ecellar_enabled()
        {
            return this.$rc.$state.isCRMEnabled('ecellar');
        },

        is_second_generation_shipping_message()
        {
            if ( this.template == null )
                return false;

            return ['shipping-exception', 'shipping-new-tracking-number','shipping-on-weather-hold','shipping-weather-hold-release','shipping-first-in-transit', 'shipping-out-for-delivery', 'shipping-picked-up', 'shipping-delivery-attempt-failed', 'shipping-available-for-pickup', 'shipping-delivered','shipping-expected-delivery-date-changed'].includes(this.template.template_context);
        },

        shipping_trigger_concept()
        {
            let context = this.template.template_context;

            if ( context == 'shipping-new-tracking-number' ) return 'can-trigger-shipping-new-tracking-number';
            if ( context == 'shipping-on-weather-hold' ) return 'can-trigger-shipping-on-weather-hold';
            if ( context == 'shipping-weather-hold-release' ) return 'can-trigger-shipping-weather-hold-release';
            if ( context == 'shipping-first-in-transit' ) return 'can-trigger-shipping-first-in-transit';
            if ( context == 'shipping-out-for-delivery' ) return 'can-trigger-shipping-out-for-delivery';
            if ( context == 'shipping-picked-up' ) return 'can-trigger-shipping-picked-up';
            if ( context == 'shipping-delivery-attempt-failed' ) return 'can-trigger-shipping-delivery-attempt-failed';
            if ( context == 'shipping-available-for-pickup' ) return 'can-trigger-shipping-available-for-pickup';
            if ( context == 'shipping-delivered' ) return 'can-trigger-shipping-delivered';
            if ( context == 'shipping-expected-delivery-date-changed' ) return 'can-trigger-shipping-expected-delivery-date-changed';

            return null;
        },

        shipping_limit_once_per_day()
        {
            let context = this.template.template_context;

            if ( context == 'shipping-out-for-delivery' ) return true;
            if ( context == 'shipping-delivery-attempt-failed' ) return true;
            if ( context == 'shipping-available-for-pickup' ) return true;

            return false;
        },
    },
}
</script>

<style lang="scss" scoped>
.question,
.answer
{
    font-weight: bold;
    margin-top: 8px;
}

.question-text,
.answer-text
{
    margin-left: 15px;
    margin-bottom: 8px;
}

</style>